import { Box, Button, Container, Grid, Skeleton, Typography } from '@mui/material';
import { Outlet, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ArrowLeft as ArrowLeftIcon } from '../../../../icons/arrow-left';
import { ExclamationOutlined as ExclamationOutlinedIcon } from '../../../../icons/exclamation-outlined';
import { useDestination } from '../../hooks/query/use-destination';
import { DestinationACS } from '../molecules/destination/destination-acs';
import { DestinationDirectionTipsCard } from '../molecules/destination/destination-direction-tips';
import { DestinationHosts } from '../molecules/destination/destination-hosts';
import { DestinationInfo } from '../molecules/destination/destination-info';
import { DestinationKiosk } from '../molecules/destination/destination-kiosk';
import { DestinationReceptions } from '../molecules/destination/destination-receptions';
import { DestinationTranslations } from '../molecules/destination/destination-translations';

import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { withPermissions } from '@/permissions/can';

const Destination = () => {
  const { t } = useTranslation(['destination']);
  const { data: organizationKiosks } = useOrganizationKiosks();
  const hasOrganizationKiosk = organizationKiosks && organizationKiosks.length > 0;

  const { navigateBack } = useNavigateBack();

  const destinationId = useParams({
    from: '/_layout/destinations/$destinationId',
    select: state => (state.destinationId ? +state.destinationId : 0),
  });

  const { data, isLoading, error } = useDestination(destinationId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ py: 4 }}>
          <Skeleton height={42} />
          <Skeleton />
          <Skeleton />
        </Box>
      );
    }

    if (error || !data) {
      return (
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}>
            <ExclamationOutlinedIcon />
            <Typography color="textSecondary" sx={{ mt: 2 }} variant="body2">
              {error?.response?.data?.message}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigateBack('/destinations')}
              variant="text">
              {t('destination:back')}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              mb: 2,
            }}>
            <Typography color="textPrimary" variant="h4">
              {data?.name}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
          </Box>
          <Grid container spacing={3}>
            <Grid
              container
              item
              lg={7}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <DestinationInfo
                  id={data.id}
                  name={data.name}
                  floor={data.floor}
                  description={data.description}
                  maxCapacity={data.maxCapacity}
                  accessBeforeVisitInMinutes={data.accessBeforeVisitInMinutes}
                  accessAfterVisitInMinutes={data.accessAfterVisitInMinutes}
                />
              </Grid>
              {hasOrganizationKiosk && (
                <Grid item xs={12}>
                  <DestinationKiosk
                    id={data.id}
                    allowedKioskIds={data.allowedKioskIds}
                    deviceIds={data.deviceIds}
                    hosts={data.hosts}
                    hostObligatory={data.hostObligatory}
                    hostConfirmationObligatory={data.hostConfirmationObligatory}
                    addHostStepEnabled={data.addHostStepEnabled}
                    hostConfirmationInEmail={data.hostConfirmationInEmail}
                    deviceConfirmation={data.deviceConfirmation}
                    defaultHostId={data.defaultHostId}
                    kioskVisitHours={data.kioskVisitHours}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <DestinationDirectionTipsCard id={data.id} directionTips={data.directionTips} />
              </Grid>
            </Grid>
            <Grid
              container
              item
              lg={5}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <DestinationACS id={data.id} accessControlProfiles={data.accessControlProfiles} />
              </Grid>
              <Grid item xs={12}>
                <DestinationHosts id={data.id} hosts={data.hosts} />
              </Grid>
              <Grid item xs={12}>
                <DestinationReceptions
                  id={data.id}
                  deviceIds={data.deviceIds}
                  deviceConfirmation={data.deviceConfirmation}
                />
              </Grid>
              <Grid item xs={12}>
                <DestinationTranslations id={data.id} translations={data.translations} />
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Outlet />
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        flexGrow: 1,
      }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
        {renderContent()}
      </Container>
    </Box>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Destinations'),
  Destination,
);
